import API from "@/utils/api";
import { buildPublicBootstrapPath } from "@/services/public-storefront-paths";

export type StorefrontBootstrapDto = {
  restaurant_id: string;
  slug: string;
  ordering_available: boolean;
};

/**
 * Explicit bootstrap entry contract. URL shape is derived only from this object —
 * never from module-level `usesHostOnlyPublicPaths()` / `entryMode`.
 */
export type StorefrontBootstrapEntry =
  | { mode: "alias"; slug: string }
  | { mode: "custom_hostname"; slug?: null };

type BootstrapEnvelope = {
  success?: boolean;
  data?: StorefrontBootstrapDto | null;
  message?: string;
  resultCode?: number;
};

/** PLATFORM_SLUG bootstrap path. Prefer {@link buildPublicBootstrapPath} for mode-aware callers. */
export function buildStorefrontBootstrapPath(slug: string): string {
  const s = slug.trim();
  if (!s) {
    throw new Error("Slug is required");
  }
  return `public/v1/storefront/bootstrap/r/${encodeURIComponent(s)}`;
}

export function buildStorefrontHostOnlyBootstrapPath(): string {
  return "public/v1/storefront/bootstrap";
}

/** Resolves the public bootstrap path from an explicit entry contract. */
export function resolveStorefrontBootstrapPath(
  entry: StorefrontBootstrapEntry
): string {
  if (entry.mode === "custom_hostname") {
    return buildStorefrontHostOnlyBootstrapPath();
  }
  return buildStorefrontBootstrapPath(entry.slug);
}

/**
 * Phase 1A / 1B.5-D2 bootstrap — server resolves PLATFORM_SLUG or TrustedHostname.
 * Client must not send RestaurantId. Path is taken from `entry`, not global entryMode.
 */
export async function fetchStorefrontBootstrap(
  entry: StorefrontBootstrapEntry,
  signal?: AbortSignal
): Promise<StorefrontBootstrapDto> {
  const path = resolveStorefrontBootstrapPath(entry);

  const response = await API.get<BootstrapEnvelope>(path, {
    signal,
    validateStatus: (status) => status >= 200 && status < 500,
  });

  if (response.status === 404 || response.status === 400) {
    const err = new Error(
      (response.data as BootstrapEnvelope)?.message || "Storefront not found."
    ) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }

  if (response.status < 200 || response.status >= 300) {
    const err = new Error(
      (response.data as BootstrapEnvelope)?.message || "Storefront bootstrap failed."
    ) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }

  const data = response.data?.data;
  const restaurantId = String(data?.restaurant_id ?? "").trim();
  const resolvedSlug = String(data?.slug ?? "").trim();

  if (!restaurantId) {
    throw new Error("Invalid storefront bootstrap response.");
  }

  // PLATFORM_SLUG requires a slug echo; CUSTOM_HOSTNAME legitimately returns empty slug.
  if (entry.mode === "alias" && !resolvedSlug) {
    throw new Error("Invalid storefront bootstrap response.");
  }

  return {
    restaurant_id: restaurantId,
    slug: resolvedSlug,
    ordering_available: Boolean(data?.ordering_available),
  };
}

// Re-export for tests that prefer the strategy module name.
export { buildPublicBootstrapPath };
